import React, { useState, useMemo } from 'react';
import { Input } from './ui/input';
import { Element as ElementType } from '../types';
import { Element as ElementComponent } from './index';
import { ScrollArea } from './ui/scroll-area';
import { Search } from 'lucide-react';
// Button import removed as it's not used
import { useDrop } from 'react-dnd';
import { useInteractionMode } from '../contexts/InteractionModeContext';

interface FavoritesPanelProps {
  favorites: ElementType[];
  onRemove: (elementId: string) => void;
  onAddToFavorites: (element: ElementType) => void;
}

const FavoritesPanel: React.FC<FavoritesPanelProps> = ({
  favorites,
  onRemove,
  onAddToFavorites,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Get interaction mode from context
  const { mode, selectedElement, setSelectedElement } = useInteractionMode();

  // Handle tap destination for favorites panel
  const handleTapDestination = () => {
    if (mode === 'tap-to-location' && selectedElement) {
      // Only handle if the element is not already in favorites
      if (!favorites.some((f) => f.id === selectedElement.id)) {
        onAddToFavorites(selectedElement);
        setSelectedElement(null);
      }
    }
  };

  const [, dropRef] = useDrop({
    accept: 'element',
    drop: (item: ElementType) => {
      onAddToFavorites(item);
      return { handled: true, name: 'FavoritesPanel' };
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
    canDrop: (item) => {
      // Don't allow drops of elements that are already in favorites
      return !favorites.some((f) => f.id === item.id);
    },
  });

  // No click-to-click functionality

  const filteredFavorites = useMemo(() => {
    if (!searchQuery.trim()) return favorites;

    const query = searchQuery.toLowerCase();
    return favorites.filter((element) => element.name.toLowerCase().includes(query));
  }, [favorites, searchQuery]);

  return (
    <div
      ref={dropRef}
      onClick={handleTapDestination}
      className={`bg-card rounded-xl shadow-lg p-4 border border-border flex flex-col h-full ${
        mode === 'tap-to-location' &&
        selectedElement &&
        !favorites.some((f) => f.id === selectedElement.id)
          ? 'cursor-pointer hover:bg-accent/10'
          : ''
      }`}
    >
      <h3 className="text-lg font-bold mb-1">Favorites ({favorites.length})</h3>
      <p className="text-xs text-muted-foreground mb-2">Drag elements here</p>

      <div className="relative mb-2">
        <Search className="absolute left-2 top-2 h-3 w-3 text-muted-foreground" />
        <Input
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-7 py-1 h-7 text-xs"
        />
      </div>

      <ScrollArea className="flex-grow max-h-[200px]">
        {filteredFavorites.length > 0 ? (
          <div className="grid grid-cols-1 gap-2 pr-1 pt-2 pb-1 px-1">
            {filteredFavorites.map((element) => (
              <div key={element.id} className="relative">
                <ElementComponent
                  element={element}
                  className="w-full min-h-10 h-auto text-sm"
                  context="machine"
                  onDragEnd={() => {
                    // This will help with dragging back to E&D
                    
                    // When dragging from favorites, we should call onRemove
                    onRemove(element.id);
                  }}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center h-40">
            <p className="text-muted-foreground">Drop elements here to add to favorites</p>
          </div>
        )}
      </ScrollArea>
    </div>
  );
};

export default FavoritesPanel;

